export function formatMoney(cents, format = "${{amount}}") {
    if (typeof cents === 'string') {
        cents = cents.replace('.', '');
    }

    const defaultOption = (opt, def) => (typeof opt === 'undefined' ? def : opt);

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        if (isNaN(number) || number === null) {
            return 0;
        }

        number = (number / 100).toFixed(precision);

        const [dollars, cents] = number.split('.');
        const formattedDollars = dollars.replace(/(\d)(?=(\d{3})+$)/g, '$1' + thousands);
        const formattedCents = cents ? decimal + cents : '';

        return formattedDollars + formattedCents;
    };

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholderRegex);

    if (!match) {
        return format;
    }

    const placeholder = match[1];
    let value;

    switch (placeholder) {
        case 'amount':
            value = formatWithDelimiters(cents);
            break;
        case 'amount_no_decimals':
            value = formatWithDelimiters(cents, 0);
            break;
        case 'amount_with_comma_separator':
            value = formatWithDelimiters(cents, 2, '.', ',');
            break;
        case 'amount_no_decimals_with_comma_separator':
            value = formatWithDelimiters(cents, 0, '.', ',');
            break;
        default:
            value = '';
    }

    return format.replace(placeholderRegex, value);
}

/**
 * Calculates and returns the price per unit text (replicates psn-price-per-unit.liquid logic)
 * @param {number} priceMin - The minimum price in cents
 * @param {number} subscriptionPriceMin - The subscription minimum price in cents (optional)
 * @param {boolean} isSubscription - Whether this is a subscription price
 * @param {number} unitTotalQuantity - Total quantity of units
 * @param {number} unitPerDay - Units per day (optional)
 * @param {number} unitServingSize - Serving size (optional)
 * @param {string} unitText - Text to append for serving (e.g., "/serving")
 * @param {string} unitPerDayText - Text to append for per day (e.g., "/day")
 * @param {string} unitDisplayType - Display type: 'per day' or 'per serving'
 * @param {string} moneyFormat - Shopify money format string
 * @returns {string} Formatted price per unit text or empty string
 */
export function getPricePerUnit({
    priceMin,
    subscriptionPriceMin = null,
    isSubscription = false,
    unitTotalQuantity = null,
    unitPerDay = null,
    unitServingSize = null,
    unitText = '',
    unitPerDayText = '',
    unitDisplayType = '',
    moneyFormat = "${{amount}}"
}) {
    // Helper to convert value to number, handling null, undefined, and string "null"
    const toNumber = (value) => {
        if (value === null || value === undefined || value === 'null' || value === '') {
            return null;
        }
        const num = typeof value === 'string' ? parseFloat(value) : Number(value);
        return isNaN(num) ? null : num;
    };
    
    // Convert priceMin to number and validate
    const price = toNumber(priceMin);
    if (price === null || price <= 0) {
        return '';
    }
    
    // Normalize unit_display_type - handle various case formats
    const displayType = (unitDisplayType || '').toLowerCase().trim();
    
    // Determine if we should show per day or per serving
    // Handle variations like "per serving", "Per serving", "per-serving", etc.
    const isPerDay = displayType === 'per day' || displayType === 'perday';
    const isPerServing = displayType === 'per serving' || displayType === 'perserving' || displayType.includes('serving');
    
    // If neither, return empty string
    if (!isPerDay && !isPerServing) {
        return '';
    }
    
    // Use subscription pricing if available and lower, otherwise use regular pricing
    let effectivePrice = price;
    if (isSubscription && subscriptionPriceMin !== null) {
        const subPrice = toNumber(subscriptionPriceMin);
        if (subPrice !== null && subPrice > 0 && subPrice < effectivePrice) {
            effectivePrice = subPrice;
        }
    }
    
    // Calculate and format per day price
    if (isPerDay) {
        const unitPerDayNum = toNumber(unitPerDay);
        
        if (unitPerDayNum !== null && unitPerDayNum > 0) {
            const pricePerUnit = effectivePrice / unitPerDayNum;
            const pricePerUnitFormatted = formatMoney(pricePerUnit, moneyFormat);
            return pricePerUnitFormatted + (unitPerDayText || '');
        }
    }
    
    // Calculate and format per serving price
    if (isPerServing) {
        const unitSize = toNumber(unitServingSize);
        const totalSize = toNumber(unitTotalQuantity);
        
        if (unitSize !== null && unitSize > 0) {
            // If totalSize is null or invalid, we can't calculate, so return empty
            if (totalSize !== null && totalSize > 0) {
                const pricePerUnit = (effectivePrice / totalSize) * unitSize;
                const pricePerUnitFormatted = formatMoney(pricePerUnit, moneyFormat);
                return pricePerUnitFormatted + (unitText || '');
            }
        }
    }
    
    return '';
}

/**
 * Gets the unit total quantity for a variant from the variant unit data
 * @param {number|string} variantId - The variant ID
 * @returns {number|null} The unit total quantity or null if not found
 */
export function getVariantUnitTotalQuantity(variantId) {
    if (!variantId) {
        return null;
    }
    
    // Try to get from the DOM element
    const variantUnitDataElement = document.querySelector("[data-variant-unit-data]");
    if (!variantUnitDataElement) {
        return null;
    }
    
    try {
        const variantUnitData = JSON.parse(variantUnitDataElement.innerHTML).variant_units;
        return variantUnitData && variantUnitData[variantId] ? variantUnitData[variantId] : null;
    } catch (e) {
        console.error("Error parsing variant unit data:", e);
        return null;
    }
}
